import { useState, useEffect } from "react";
import { supabase } from "@/integrations/supabase/client";

export type UserRole = 'owner' | 'admin' | 'premium' | 'user';

interface UseUserRoleResult {
  roles: UserRole[];
  isOwner: boolean;
  isAdmin: boolean;
  isPremium: boolean;
  loading: boolean;
  refetch: () => Promise<void>;
}

export const useUserRole = (userId?: string): UseUserRoleResult => {
  const [roles, setRoles] = useState<UserRole[]>([]);
  const [loading, setLoading] = useState(true);

  const fetchRoles = async () => {
    if (!userId) {
      setRoles([]);
      setLoading(false);
      return;
    }

    try {
      const { data, error } = await supabase
        .from('user_roles')
        .select('role')
        .eq('user_id', userId);

      if (error) throw error;

      const userRoles = data?.map(r => r.role as UserRole) || [];
      setRoles(userRoles);
    } catch (error) {
      console.error('Error fetching user roles:', error);
      setRoles([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchRoles();
  }, [userId]);

  return {
    roles,
    isOwner: roles.includes('owner'),
    isAdmin: roles.includes('admin') || roles.includes('owner'),
    isPremium: roles.includes('premium') || roles.includes('admin') || roles.includes('owner'),
    loading,
    refetch: fetchRoles
  };
};
